/**
 * DailyChallengeManager — client-side state for daily challenges.
 *
 * Follows the same dual-storage pattern as ScoreManager/ProgressManager:
 * localStorage as primary (instant, offline), server as secondary.
 *
 * API:
 *   fetchChallenge()           → today's challenge config
 *   summarizeStage(stageResult) → display/submit summary of a StageSim result
 *   submitScore(stageResult)   → submit scored attempt (once per day)
 *   fetchLeaderboard(date, limit) → daily leaderboard
 *   hasPlayedToday()           → check localStorage
 *   getStreak()                → cached streak info
 *   generateShareText(summary) → Wordle-style share with an energy bar
 */

const STORAGE_CHALLENGE = "woodchuck_daily_challenge";
const STORAGE_RESULT = "woodchuck_daily_result";
const STORAGE_STREAK = "woodchuck_daily_streak";

// Difficulty → simulated level mapping
const DIFFICULTY_LEVELS = {
  easy: 5,
  medium: 15,
  hard: 25,
  extreme: 35,
};

// Share card energy bar: one block per 1,000 Wh, ten blocks
const WH_PER_ENERGY_BLOCK = 1000;
const ENERGY_BLOCKS = 10;

const toNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** How a stage ended, in arcade words. */
function stageOutcome(r) {
  if (r.status === "complete") return "CLEARED";
  if (r.kind === "defend") return "DRAINED";
  // Build stages fail when the reserve floods out or the last life is lost
  return toNumber(r.lives) <= 0 && toNumber(r.reserveWh) > 0 ? "KNOCKED OUT" : "FLOODED";
}

function getTodayDate() {
  return new Date().toISOString().slice(0, 10);
}

function getAuthHeaders() {
  const token = localStorage.getItem("woodchuck_session");
  if (!token) return { "Content-Type": "application/json" };
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

const DailyChallengeManager = {
  /**
   * Fetch today's challenge from server, with localStorage cache.
   * @returns {Promise<{ date, celebrity_index, seed, difficulty, level }>}
   */
  async fetchChallenge() {
    const cached = this._getCachedChallenge();
    if (cached && cached.date === getTodayDate()) {
      return cached;
    }

    try {
      const resp = await fetch("/api/daily");
      if (resp.ok) {
        const data = await resp.json();
        const challenge = {
          ...data,
          level: DIFFICULTY_LEVELS[data.difficulty] || 15,
        };
        localStorage.setItem(STORAGE_CHALLENGE, JSON.stringify(challenge));
        return challenge;
      }
    } catch {
      // Fall through to fallback
    }

    // Offline fallback: generate deterministically
    return this._generateFallback();
  },

  /**
   * Reduce a StageSim `result()` object to what the daily challenge shows,
   * shares and submits. Score is the stage's WOOD logs.
   * @param {Object} stageResult - StageSim result() (status, kind, woodLogs, totalWh, timeMs, ...)
   * @returns {{ score, woodLogs, totalWh, timeSeconds, cleared, outcome, kind, level }}
   */
  summarizeStage(stageResult) {
    const r = stageResult || {};
    const woodLogs = Math.max(0, Math.floor(toNumber(r.woodLogs)));
    return {
      score: woodLogs,
      woodLogs,
      totalWh: Math.max(0, toNumber(r.totalWh)),
      timeSeconds: Math.round(Math.max(0, toNumber(r.timeMs)) / 100) / 10,
      cleared: r.status === "complete",
      outcome: stageOutcome(r),
      kind: r.kind || "build",
      level: toNumber(r.level) || 1,
    };
  },

  /**
   * Submit today's scored attempt.
   * @param {Object} stageResult - StageSim result() of the daily stage
   * @returns {Promise<Object>} the summary plus { rank, totalPlayers, streakInfo }, or { error }
   */
  async submitScore(stageResult) {
    if (this.hasPlayedToday()) {
      return { error: "Already submitted today", alreadyPlayed: true };
    }

    const summary = this.summarizeStage(stageResult);
    const result = { ...summary, date: getTodayDate(), submitted: true };

    try {
      const resp = await fetch("/api/daily/submit", {
        method: "POST",
        headers: getAuthHeaders(),
        // comboMax/kpm are legacy columns, kept for API compatibility
        body: JSON.stringify({
          score: summary.score,
          comboMax: 0,
          kpm: 0,
          timeSeconds: summary.timeSeconds,
          cleared: summary.cleared,
        }),
      });

      if (resp.ok) {
        const data = await resp.json();
        const fullResult = { ...result, ...data };
        localStorage.setItem(STORAGE_RESULT, JSON.stringify(fullResult));

        // Cache streak
        if (data.streakInfo) {
          localStorage.setItem(STORAGE_STREAK, JSON.stringify(data.streakInfo));
        }

        return fullResult;
      }

      // 409 = already played
      if (resp.status === 409) {
        localStorage.setItem(STORAGE_RESULT, JSON.stringify(result));
        return { error: "Already submitted today", alreadyPlayed: true };
      }

      const errData = await resp.json().catch(() => ({}));
      return { error: errData.error || "Submission failed" };
    } catch {
      // Save locally even if server fails
      localStorage.setItem(STORAGE_RESULT, JSON.stringify(result));
      return { ...result, rank: null, totalPlayers: null };
    }
  },

  /**
   * Fetch daily leaderboard.
   * @param {string} [date] - YYYY-MM-DD, defaults to today
   * @param {number} [limit=20]
   * @returns {Promise<{ date, totalPlayers, leaderboard }>}
   */
  async fetchLeaderboard(date, limit = 20) {
    const d = date || getTodayDate();
    try {
      const resp = await fetch(`/api/daily/leaderboard?date=${d}&limit=${limit}`);
      if (resp.ok) {
        return await resp.json();
      }
    } catch {
      // Fall through
    }
    return { date: d, totalPlayers: 0, leaderboard: [] };
  },

  /**
   * Check if player has already submitted a scored attempt today.
   * @returns {boolean}
   */
  hasPlayedToday() {
    try {
      const data = localStorage.getItem(STORAGE_RESULT);
      if (!data) return false;
      const result = JSON.parse(data);
      return result.date === getTodayDate() && result.submitted;
    } catch {
      return false;
    }
  },

  /**
   * Get today's result (if submitted).
   * @returns {{ score, rank, totalPlayers, streakInfo } | null}
   */
  getTodayResult() {
    try {
      const data = localStorage.getItem(STORAGE_RESULT);
      if (!data) return null;
      const result = JSON.parse(data);
      if (result.date !== getTodayDate()) return null;
      return result;
    } catch {
      return null;
    }
  },

  /**
   * Get cached streak info.
   * @returns {{ currentStreak, longestStreak, streakBonus } | null}
   */
  getStreak() {
    try {
      const data = localStorage.getItem(STORAGE_STREAK);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  },

  /**
   * Generate Wordle-style share text.
   * @param {{ woodLogs: number, totalWh: number, timeSeconds: number, outcome: string }} summary
   *   from summarizeStage()
   * @returns {string}
   */
  generateShareText(summary) {
    const today = getTodayDate();
    const dayNum = Math.floor(
      (new Date(today).getTime() - new Date("2025-01-01").getTime()) /
        (1000 * 60 * 60 * 24),
    );
    const woodLogs = toNumber(summary.woodLogs);

    return [
      `Woodchuck Daily #${dayNum} - Dams to AGI`,
      this._energyBar(summary.totalWh),
      `\ud83e\udeb5 ${woodLogs.toLocaleString()} WOOD | ${summary.outcome || "FLOODED"} | ${toNumber(summary.timeSeconds).toFixed(1)}s`,
      "howmuchwoodcouldawoodchuckchuck.com",
    ].join("\n");
  },

  /**
   * Emoji energy bar: one lightning bolt per 1,000 Wh, black squares for the rest.
   * @param {number} totalWh
   * @returns {string}
   */
  _energyBar(totalWh) {
    const filled = Math.min(ENERGY_BLOCKS, Math.max(0, Math.floor(toNumber(totalWh) / WH_PER_ENERGY_BLOCK)));
    return "\u26a1".repeat(filled) + "\u2b1b".repeat(ENERGY_BLOCKS - filled);
  },

  // ── Private Helpers ──────────────────────────────────────

  _getCachedChallenge() {
    try {
      const data = localStorage.getItem(STORAGE_CHALLENGE);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  },

  _generateFallback() {
    const today = getTodayDate();
    let h = 0;
    const str = "woodchuck_daily_" + today;
    for (let i = 0; i < str.length; i++) {
      h = ((h << 5) - h + str.charCodeAt(i)) | 0;
    }
    const seed = Math.abs(h);
    const day = new Date(today + "T00:00:00Z").getUTCDay();
    const difficulties = [
      "medium",
      "easy",
      "medium",
      "medium",
      "hard",
      "hard",
      "extreme",
    ];
    const difficulty = difficulties[day];

    return {
      date: today,
      celebrity_index: seed % 100,
      seed,
      difficulty,
      level: DIFFICULTY_LEVELS[difficulty] || 15,
    };
  },
};

export default DailyChallengeManager;
